import { supabase } from './supabase';

export type UserRole = 'user' | 'support' | 'admin';

export interface User {
  id: string;
  email: string;
  full_name?: string;
  company_name?: string;
  role: UserRole;
  is_active: boolean;
  created_at: string;
}

async function fetchProfile(userId: string): Promise<User | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, email, full_name, company_name, role, is_active, created_at')
    .eq('id', userId)
    .maybeSingle();

  if (error || !data) return null;

  return {
    id: data.id,
    email: data.email,
    full_name: data.full_name ?? undefined,
    company_name: data.company_name ?? undefined,
    role: data.role as UserRole,
    is_active: data.is_active,
    created_at: data.created_at,
  };
}

class AuthService {
  async signIn(email: string, password: string): Promise<User> {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message);

    const profile = await fetchProfile(data.user.id);
    if (!profile) throw new Error('Profile not found. Please contact support.');
    return profile;
  }

  async signUp(email: string, password: string, fullName?: string): Promise<User> {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName ?? '' } },
    });
    if (error) throw new Error(error.message);
    if (!data.user) throw new Error('Sign up failed. Please try again.');

    // If a session was returned, set it so subsequent RLS-authenticated calls work.
    if (data.session) {
      await supabase.auth.setSession(data.session);
    }

    // Profile is auto-created by the handle_new_user trigger.
    // Poll briefly to allow the trigger to execute.
    let profile: User | null = null;
    for (let i = 0; i < 8; i++) {
      await new Promise(r => setTimeout(r, 500));
      profile = await fetchProfile(data.user.id);
      if (profile) break;
    }
    if (!profile) throw new Error('Profile creation failed. Please try again.');
    return profile;
  }

  async signOut(): Promise<void> {
    await supabase.auth.signOut();
  }

  async getCurrentUser(): Promise<User | null> {
    const { data } = await supabase.auth.getSession();
    if (!data.session?.user) return null;
    return fetchProfile(data.session.user.id);
  }

  onAuthStateChanged(callback: (user: User | null) => void): () => void {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      (async () => {
        if (session?.user) {
          const profile = await fetchProfile(session.user.id);
          callback(profile);
        } else {
          callback(null);
        }
      })();
    });
    return () => subscription.unsubscribe();
  }
}

export const authService = new AuthService();
